import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";

import { apiClient } from "../api/httpClient";
import Navbar from "../globals/components/Navbar";
import { capitalizeFirst } from "./Law Firm User/helpers/lawFirmBondsHelpers";
import useBondArchives, { normalizePaymentStatusKey } from "./hooks/useBondArchives";
import BondArchivesToolbar from "./components/BondArchivesToolbar";
import BondArchivesTable from "./components/BondArchivesTable";
import BondFilterDialog from "./components/BondFilterDialog";
import BondArchivesCancelDialog from "./components/BondArchivesCancelDialog";
import BondArchivesEndorsementDialog from "./components/BondArchivesEndorsementDialog";
import BondArchivesDeleteDialog from "./components/BondArchivesDeleteDialog";
import BondArchivesLibertyConfirmDialog from "./components/BondArchivesLibertyConfirmDialog";
import BondArchivesBypassHistoryDialog from "./components/BondArchivesBypassHistoryDialog";
import BondArchivesPaymentOverrideHistoryDialog from "./components/BondArchivesPaymentOverrideHistoryDialog";

const BondArchives = () => {
  const {
    applyFilters, attorneys, availableEstates, availablePrincipals, availableSubcategories,
    bondNumberOptionsArchive, bondTypes, bypassHistoryDialogOpen, bypassHistoryList, bypassHistoryLoading, bypassHistorySearch,
    cancellationFile, cancelDialogOpen, clearFilters, contactEmail, contactName, counties,
    deleteDialogOpen, dispatch, dispatchArchiveFilters, dwbAlias, dwbFirstName, dwbLastName, dwbMiddleName, dwbSuffix,
    endorsementDialogOpen, endorsementFile, endorsementToCounty, endorsementType, estimatedAssets,
    filteredData, filteredRows, filterDialogOpen, filters, fetchInitialData, getCancellationInstructionsList,
    getEndorsementInstructions, getPaymentStatusChipProps, handleCancellationFileInputChange, handleCancelIconClick,
    handleDeleteClick, handleDeleteClose, handleDeleteConfirm, handleEndorseIconClick,
    handleEndorsementSupportingFileChange, handleExport, handleLibertyBypassConfirm,
    handleLibertyIconClick, handleMarkCancellationComplete, handleMarkToBeCancelled,
    handleOpenBypassHistory, handleOpenPaymentOverrideHistory, handleRefreshClick, handleReleaseRider, handleRevertCancellation,
    handleReviewBond, handleSubmitCancellationRequest, handleSort, handleSubmitEndorsementRequest,
    handleViewBond, internalUsers, isCancelling, isDeleting, isMarkingCancellationComplete,
    isMarkingLibertyComplete, isMarkingToBeCancelled, isSubmittingEndorsement, lawFirms,
    libertyConfirmDialogOpen, loading, loadingCounties, newBondAmount, order, orderBy,     page,
    paymentOverrideHistoryList,
    paymentOverrideHistoryLoading,
    paymentOverrideHistoryOpen,
    paymentOverrideHistorySearch,
    professionalGuardians, role, rowsPerPage, selectedBondToCancel,
    selectedBondToEndorse, setBypassHistoryDialogOpen, setBypassHistorySearch, setCancelDialogOpen,
    setPaymentOverrideHistoryOpen, setPaymentOverrideHistorySearch,
    setContactEmail, setContactName, setDwbAlias, endorsementPrincipalId, setEndorsementPrincipalId,
    setDwbFirstName, setDwbLastName,
    setDwbMiddleName, setDwbSuffix, setEndorsementDialogOpen,
    setEndorsementToCounty, setEndorsementType, setEstimatedAssets, setFilterDialogOpen,
    setLibertyConfirmDialogOpen, setNewBondAmount, setPage, setRowsPerPage,
    setSelectedBondToLiberty, setUpdatedName, statusOptions, tableColumns, updatedName,
  } = useBondArchives();

  const canOverridePaymentStatus =
    role === "internal_user" || role === "super_user";

  const [paymentOverrideOpen, setPaymentOverrideOpen] = useState(false);
  const [paymentOverrideBond, setPaymentOverrideBond] = useState(null);
  const [paymentOverrideNewStatus, setPaymentOverrideNewStatus] = useState("paid");
  const [paymentOverrideReason, setPaymentOverrideReason] = useState("");
  const [paymentOverrideSubmitting, setPaymentOverrideSubmitting] = useState(false);

  useEffect(() => {
    if (paymentOverrideOpen && paymentOverrideBond) {
      const k = normalizePaymentStatusKey(paymentOverrideBond.payment_status);
      const allowed = ["paid", "unpaid", "na"];
      setPaymentOverrideNewStatus(allowed.includes(k) ? k : "paid");
      setPaymentOverrideReason("");
    }
  }, [paymentOverrideOpen, paymentOverrideBond]);

  const handlePaymentStatusOverrideClick = (bond) => {
    setPaymentOverrideBond(bond);
    setPaymentOverrideOpen(true);
  };

  const handlePaymentOverrideClose = () => {
    setPaymentOverrideOpen(false);
    setPaymentOverrideBond(null);
    setPaymentOverrideReason("");
    setPaymentOverrideSubmitting(false);
  };

  const handlePaymentOverrideSubmit = async () => {
    if (!paymentOverrideBond?.bond_id || !paymentOverrideReason.trim()) {
      toast.error("Please enter a reason for the override.");
      return;
    }
    setPaymentOverrideSubmitting(true);
    try {
      await apiClient("/liberty-payment-status/override/", "POST", {
        bond_id: paymentOverrideBond.bond_id,
        new_status: paymentOverrideNewStatus,
        reason: paymentOverrideReason.trim(),
        overridden_by: "admin",
      });
      toast.success("Payment status updated.");
      handlePaymentOverrideClose();
      await fetchInitialData();
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        error?.message;
      toast.error(detail || "Failed to update payment status.");
    } finally {
      setPaymentOverrideSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "60vw" }}>
            <Typography variant="h4" fontWeight="bold">
              Bond Archives
            </Typography>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Here are all the bonds that have been processed through the system to date.
            </span>
          </Box>
        </Box>

        <BondArchivesToolbar
          handleOpenBypassHistory={handleOpenBypassHistory}
          canOverridePaymentStatus={canOverridePaymentStatus}
          handleOpenPaymentOverrideHistory={handleOpenPaymentOverrideHistory}
          handleExport={handleExport}
          handleRefreshClick={handleRefreshClick}
          loading={loading}
          setFilterDialogOpen={setFilterDialogOpen}
        />

        <BondArchivesTable
          tableColumns={tableColumns}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          loading={loading}
          filteredRows={filteredRows}
          filteredData={filteredData}
          page={page}
          setPage={setPage}
          dispatch={dispatch}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          isMarkingLibertyComplete={isMarkingLibertyComplete}
          handleViewBond={handleViewBond}
          handleLibertyIconClick={handleLibertyIconClick}
          handleCancelIconClick={handleCancelIconClick}
          handleEndorseIconClick={handleEndorseIconClick}
          handleDeleteClick={handleDeleteClick}
          role={role}
          getPaymentStatusChipProps={getPaymentStatusChipProps}
          onPaymentStatusOverrideClick={handlePaymentStatusOverrideClick}
        />

        <BondFilterDialog
          variant="bondArchives"
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          filters={filters}
          onFiltersChange={dispatchArchiveFilters}
          bondTypes={bondTypes}
          availableSubcategories={availableSubcategories}
          bondNumberOptionsArchive={bondNumberOptionsArchive}
          bondNumberOptionsLawFirm={[]}
          availablePrincipals={availablePrincipals}
          availableEstates={availableEstates}
          principalFilterOptions={[]}
          estateFilterOptions={[]}
          lawFirms={lawFirms}
          attorneys={attorneys}
          professionalGuardians={professionalGuardians}
          internalUsers={internalUsers}
          statusOptions={statusOptions}
          lawFirmStatusOptions={[]}
          archiveStatusOptions={[]}
          capitalizeFirst={capitalizeFirst}
          clearFilters={clearFilters}
          applyFilters={() => void applyFilters()}
        />

        <BondArchivesCancelDialog
          cancelDialogOpen={cancelDialogOpen}
          setCancelDialogOpen={setCancelDialogOpen}
          isCancelling={isCancelling}
          isMarkingCancellationComplete={isMarkingCancellationComplete}
          isMarkingToBeCancelled={isMarkingToBeCancelled}
          selectedBondToCancel={selectedBondToCancel}
          getCancellationInstructionsList={getCancellationInstructionsList}
          handleCancellationFileInputChange={handleCancellationFileInputChange}
          cancellationFile={cancellationFile}
          handleMarkToBeCancelled={handleMarkToBeCancelled}
          handleMarkCancellationComplete={handleMarkCancellationComplete}
          handleRevertCancellation={handleRevertCancellation}
          handleSubmitCancellationRequest={handleSubmitCancellationRequest}
        />

        <BondArchivesEndorsementDialog
          endorsementDialogOpen={endorsementDialogOpen}
          setEndorsementDialogOpen={setEndorsementDialogOpen}
          isSubmittingEndorsement={isSubmittingEndorsement}
          selectedBondToEndorse={selectedBondToEndorse}
          endorsementType={endorsementType}
          setEndorsementType={setEndorsementType}
          counties={counties}
          loadingCounties={loadingCounties}
          endorsementToCounty={endorsementToCounty}
          setEndorsementToCounty={setEndorsementToCounty}
          updatedName={updatedName}
          setUpdatedName={setUpdatedName}
          dwbFirstName={dwbFirstName}
          setDwbFirstName={setDwbFirstName}
          dwbMiddleName={dwbMiddleName}
          setDwbMiddleName={setDwbMiddleName}
          dwbLastName={dwbLastName}
          setDwbLastName={setDwbLastName}
          dwbSuffix={dwbSuffix}
          setDwbSuffix={setDwbSuffix}
          dwbAlias={dwbAlias}
          setDwbAlias={setDwbAlias}
          endorsementPrincipalId={endorsementPrincipalId}
          setEndorsementPrincipalId={setEndorsementPrincipalId}
          newBondAmount={newBondAmount}
          setNewBondAmount={setNewBondAmount}
          estimatedAssets={estimatedAssets}
          setEstimatedAssets={setEstimatedAssets}
          getEndorsementInstructions={getEndorsementInstructions}
          handleEndorsementSupportingFileChange={handleEndorsementSupportingFileChange}
          endorsementFile={endorsementFile}
          contactName={contactName}
          setContactName={setContactName}
          contactEmail={contactEmail}
          setContactEmail={setContactEmail}
          role={role}
          handleReleaseRider={handleReleaseRider}
          handleSubmitEndorsementRequest={handleSubmitEndorsementRequest}
        />

        <BondArchivesDeleteDialog
          deleteDialogOpen={deleteDialogOpen}
          isDeleting={isDeleting}
          handleDeleteClose={handleDeleteClose}
          handleReviewBond={handleReviewBond}
          handleDeleteConfirm={handleDeleteConfirm}
        />

        <BondArchivesLibertyConfirmDialog
          libertyConfirmDialogOpen={libertyConfirmDialogOpen}
          setLibertyConfirmDialogOpen={setLibertyConfirmDialogOpen}
          setSelectedBondToLiberty={setSelectedBondToLiberty}
          isMarkingLibertyComplete={isMarkingLibertyComplete}
          handleLibertyBypassConfirm={handleLibertyBypassConfirm}
        />

        <BondArchivesBypassHistoryDialog
          bypassHistoryDialogOpen={bypassHistoryDialogOpen}
          setBypassHistoryDialogOpen={setBypassHistoryDialogOpen}
          bypassHistorySearch={bypassHistorySearch}
          setBypassHistorySearch={setBypassHistorySearch}
          bypassHistoryLoading={bypassHistoryLoading}
          bypassHistoryList={bypassHistoryList}
        />

        <BondArchivesPaymentOverrideHistoryDialog
          paymentOverrideHistoryOpen={paymentOverrideHistoryOpen}
          setPaymentOverrideHistoryOpen={setPaymentOverrideHistoryOpen}
          paymentOverrideHistorySearch={paymentOverrideHistorySearch}
          setPaymentOverrideHistorySearch={setPaymentOverrideHistorySearch}
          paymentOverrideHistoryLoading={paymentOverrideHistoryLoading}
          paymentOverrideHistoryList={paymentOverrideHistoryList}
        />

        <Dialog
          open={paymentOverrideOpen}
          onClose={paymentOverrideSubmitting ? undefined : handlePaymentOverrideClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Override payment status</DialogTitle>
          <DialogContent
            dividers
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 3,
              overflow: "visible",
            }}
          >
            <TextField
              select
              label="New status"
              fullWidth
              value={paymentOverrideNewStatus}
              onChange={(e) => setPaymentOverrideNewStatus(e.target.value)}
              disabled={paymentOverrideSubmitting}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="na">N/A</MenuItem>
            </TextField>
            <TextField
              label="Reason"
              value={paymentOverrideReason}
              onChange={(e) => setPaymentOverrideReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
              disabled={paymentOverrideSubmitting}
              placeholder="Explain why this override is being recorded"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePaymentOverrideClose} disabled={paymentOverrideSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePaymentOverrideSubmit}
              disabled={paymentOverrideSubmitting}
            >
              {paymentOverrideSubmitting ? <CircularProgress size={22} /> : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default BondArchives;
